import { useEffect, useMemo, useState } from 'react'
import { addDays, format } from 'date-fns'
import UniqueValueInfo from '@arcgis/core/renderers/support/UniqueValueInfo'
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer'
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'

import { IColourClassificationOptions, ISelectOption } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useHMCABaselineInfoQuery } from 'app/services/api/baselines'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import { formatHexToEsriRgb } from 'common/utils/colorUtils'
import { createBatches } from 'common/utils/mapUtils'

import MapComponent from 'features/Map'
import { IUniqueValueInfo } from 'features/Map/interfaces/Maps'
import { selectBaseSettings } from 'features/Settings/settingsSelectors'

import { HMCATableViewMode } from './Tables'

interface IMapWrapper {
  selectedDateRange: Array<Date | null>
  selectedLines: ISelectOption[]
  selectedClassifications: ISelectOption[]
  tableMode: HMCATableViewMode
  mapAttributeId: number | undefined
}

const ROUTE_QUERY_BATCH_SIZE = 1000

const MapWrapper = ({
  selectedDateRange,
  selectedLines,
  selectedClassifications,
  tableMode,
  mapAttributeId
}: IMapWrapper) => {
  const [uniqueValueInfos, setUniqueValueInfos] = useState<IUniqueValueInfo[]>()
  const baseSettings = useAppSelector(selectBaseSettings)
  const [filterQuery, setFilterQuery] = useState('')

  const [showStructureLayer, setShowStructureLayer] = useState(false)

  const HMCARendererOptions = useMemo(
    () => ({
      legendTitle: 'Consequence Area',
      field: 'ConsequenceAreaCode'
    }),
    []
  )

  const ClassLocationRendererOptions = useMemo(
    () => ({
      legendTitle: 'Class Location',
      field: 'ClassLocationCode'
    }),
    []
  )

  const rendererOptions = useMemo(
    () =>
      tableMode === HMCATableViewMode.HMCA
        ? HMCARendererOptions
        : ClassLocationRendererOptions,
    [tableMode, HMCARendererOptions, ClassLocationRendererOptions]
  )

  const contentFields = useMemo(
    () => [
      {
        fieldName: 'RouteName',
        label: 'Route Name'
      },
      {
        fieldName: 'FromMeasure',
        label: 'From Measure',
        format: {
          places: 3,
          digitSeparator: true
        }
      },
      {
        fieldName: 'ToMeasure',
        label: 'To Measure',
        format: {
          places: 3,
          digitSeparator: true
        }
      },
      {
        fieldName: 'FromDate',
        label: 'From Date'
      },
      {
        fieldName: 'ToDate',
        label: 'To Date'
      },
      {
        fieldName: rendererOptions.field,
        label: 'Classification'
      },
      {
        fieldName: 'expression/potentialImpactRadiusFormatted',
        label: 'Potential Impact Radius (ft)'
      }
    ],
    [rendererOptions]
  )

  const popupExpressionInfos = useMemo(
    () => [
      {
        name: 'potentialImpactRadiusFormatted',
        title: 'Potential Impact Radius (ft)',
        expression:
          "IIF(IsEmpty($feature.PotentialImpactRadius), '', Text($feature.PotentialImpactRadius, '#,###.#'))"
      }
    ],
    []
  )

  const { currentData: baseline } = useHMCABaselineInfoQuery(tableMode)

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  useEffect(() => {
    const lookupTables = baseSettings.find(
      (setting) => setting.groupName === 'LookupTables'
    )

    const allOptions = lookupTables?.details.find(
      (table) => table.key === 'ClassificationColour'
    )?.value as unknown as IColourClassificationOptions[]

    if (mapAttributeId && allOptions) {
      const optionAttributes = allOptions.filter(
        (option) => option.EventTableAttributeId === mapAttributeId
      )

      const options: IUniqueValueInfo[] = optionAttributes.map((option) => {
        return {
          label: option.Code,
          value: option.Code,
          color: option.Colour,
          width: 4
        }
      })

      setUniqueValueInfos(options)
    }
  }, [baseSettings, mapAttributeId])

  const memoizedRenderer = useMemo(() => {
    if (uniqueValueInfos !== undefined) {
      return new UniqueValueRenderer({
        legendOptions: {
          title: rendererOptions?.legendTitle ?? ''
        },
        field: rendererOptions?.field,
        uniqueValueInfos: uniqueValueInfos.map((item) => {
          return new UniqueValueInfo({
            label: item.label,
            value: item.value,
            symbol: new CIMSymbol({
              data: {
                type: 'CIMSymbolReference',
                symbol: {
                  type: 'CIMLineSymbol',
                  symbolLayers: [
                    {
                      type: 'CIMSolidStroke',
                      enable: true,
                      capStyle: 'Butt',
                      joinStyle: 'Round',
                      width: item.width,
                      color: formatHexToEsriRgb(item.color)
                    }
                  ]
                }
              }
            })
          })
        })
      })
    }

    return new UniqueValueRenderer()
  }, [uniqueValueInfos, rendererOptions])

  useEffect(() => {
    let queryString = ''

    // Effective From/To Date Range filter
    if (selectedDateRange !== undefined && selectedDateRange[0] != null) {
      let dateRangeQueryString = ''

      const isValidDate = (d: Date | null) =>
        d instanceof Date && !isNaN(d as unknown as number)

      // FromDate and ToDate selected
      if (isValidDate(selectedDateRange[0])) {
        const fromDate: string = format(selectedDateRange[0]!, 'yyyy-MM-dd')

        dateRangeQueryString += `(FromDate >= DATE '${fromDate}'`

        if (isValidDate(selectedDateRange[1])) {
          const tommorrowDate: Date = addDays(
            new Date(selectedDateRange[1]!),
            1
          )

          const toDate = format(tommorrowDate, 'yyyy-MM-dd')

          dateRangeQueryString += ` AND ToDate < DATE '${toDate}')`
        } else {
          dateRangeQueryString += ')'
        }
      }

      queryString =
        queryString === ''
          ? dateRangeQueryString
          : `${queryString} AND ${dateRangeQueryString}`
    } else {
      queryString =
        queryString === ''
          ? 'ToDate IS NULL'
          : `${queryString} AND ToDate IS NULL`
    }

    // Consequence Area filter
    if (rendererOptions?.field === 'ConsequenceAreaCode') {
      if (
        selectedClassifications !== undefined &&
        selectedClassifications.length > 0
      ) {
        let hmcaQueryString = ''

        selectedClassifications.forEach((hmca) => {
          hmcaQueryString += `ConsequenceAreaCode = '${hmca.value}' OR `
        })

        hmcaQueryString = `(${hmcaQueryString.slice(0, -4)})`

        queryString =
          queryString === ''
            ? hmcaQueryString
            : `${queryString} AND ${hmcaQueryString}`
      }
      // Class Location filter
    } else if (rendererOptions?.field === 'ClassLocationCode') {
      if (
        selectedClassifications !== undefined &&
        selectedClassifications.length > 0
      ) {
        let classLocationQueryString = ''

        selectedClassifications.forEach((classLocation) => {
          classLocationQueryString += `ClassLocationCode = '${classLocation.value}' OR `
        })

        classLocationQueryString = `(${classLocationQueryString.slice(0, -4)})`

        queryString =
          queryString === ''
            ? classLocationQueryString
            : `${queryString} AND ${classLocationQueryString}`
      }
    }

    setFilterQuery(queryString)
  }, [rendererOptions?.field, selectedClassifications, selectedDateRange])

  const featureLayerFilterQueries = useMemo(() => {
    const routeIds = selectedLines.length
      ? selectedLines.map((line) => line.value)
      : (systemScopeFilterProfile?.continuousRoutes ?? []).map(
          (route) => route.id
        )

    const routeQueries = createBatches(routeIds, ROUTE_QUERY_BATCH_SIZE).map(
      (batch) =>
        `RouteId IN (${batch
          .map((id) => `'${String(id).replaceAll("'", "''")}'`)
          .join(',')})`
    )

    if (!routeQueries.length) {
      return [filterQuery]
    }

    return routeQueries.map((routeQuery) =>
      filterQuery ? `${routeQuery} AND ${filterQuery}` : routeQuery
    )
  }, [filterQuery, selectedLines, systemScopeFilterProfile?.continuousRoutes])

  return (
    <MapComponent
      featureLayerUrl={baseline?.featureLayerUrl}
      featureLayerFilterQueries={featureLayerFilterQueries}
      renderer={memoizedRenderer}
      featureLayerTitle={rendererOptions.legendTitle}
      contentFields={contentFields}
      popupExpressionInfos={popupExpressionInfos}
      showStructureLayer={showStructureLayer}
      onStructureLayerVisibilityChange={setShowStructureLayer}
    />
  )
}

export default MapWrapper
